/**
 * PrimitiveMappingEditor Component
 * 
 * Editor for mapping inputs/outputs between scenario and primitive.
 * - Input Mapping: Map scenario variables → primitive inputs
 * - Output Mapping: Map primitive outputs → scenario variables
 */

import React, { useState } from 'react';
import { JSONSchema } from '@simcheck/shared-types';

interface PrimitiveMappingEditorProps {
  inputSchema?: JSONSchema;
  outputSchema?: JSONSchema;
  inputMapping?: Record<string, string>;
  outputMapping?: Record<string, string>;
  onInputMappingChange: (mapping: Record<string, string>) => void;
  onOutputMappingChange: (mapping: Record<string, string>) => void;
}

export const PrimitiveMappingEditor: React.FC<PrimitiveMappingEditorProps> = ({
  inputSchema,
  outputSchema,
  inputMapping = {},
  outputMapping = {},
  onInputMappingChange,
  onOutputMappingChange,
}) => {
  const [localInputMapping, setLocalInputMapping] = useState<Record<string, string>>(inputMapping);
  const [localOutputMapping, setLocalOutputMapping] = useState<Record<string, string>>(outputMapping);

  const inputProperties = inputSchema?.properties || {};
  const outputProperties = outputSchema?.properties || {};

  const handleInputMappingChange = (paramName: string, value: string) => {
    const newMapping = { ...localInputMapping, [paramName]: value };
    if (!value.trim()) {
      delete newMapping[paramName];
    }
    setLocalInputMapping(newMapping);
    onInputMappingChange(newMapping);
  };

  const handleOutputMappingChange = (outputName: string, value: string) => {
    const newMapping = { ...localOutputMapping, [outputName]: value };
    if (!value.trim()) {
      delete newMapping[outputName];
    }
    setLocalOutputMapping(newMapping);
    onOutputMappingChange(newMapping);
  };

  const hasInputs = Object.keys(inputProperties).length > 0;
  const hasOutputs = Object.keys(outputProperties).length > 0;

  if (!hasInputs && !hasOutputs) {
    return (
      <div className="bg-muted rounded-lg border border-border p-4 text-center">
        <p className="text-sm text-muted-foreground/70 italic">
          This primitive has no inputs or outputs to configure
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Input Mapping */}
      {hasInputs && (
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">
            Input Mapping
            <span className="ml-2 text-xs font-normal text-muted-foreground/70">
              Map scenario variables to primitive inputs
            </span>
          </h4>
          <div className="space-y-3">
            {Object.entries(inputProperties).map(([paramName, paramDef]) => {
              const isRequired = inputSchema?.required?.includes(paramName);
              return (
                <div
                  key={paramName}
                  className="bg-muted rounded-lg border border-border p-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <label
                          htmlFor={`input-${paramName}`}
                          className="text-sm font-medium text-foreground"
                        >
                          {paramName}
                        </label>
                        <span className="text-xs px-1.5 py-0.5 bg-accent-blue/10 dark:bg-accent-blue/20 text-accent-blue rounded font-mono">
                          {paramDef.type}
                        </span>
                        {isRequired && (
                          <span className="text-xs px-1.5 py-0.5 bg-status-error-bg text-status-error rounded font-semibold">
                            required
                          </span>
                        )}
                      </div>
                      {paramDef.description && (
                        <p className="text-xs text-muted-foreground mb-2">
                          {paramDef.description}
                        </p>
                      )}
                      <input
                        type="text"
                        id={`input-${paramName}`}
                        value={localInputMapping[paramName] || ''}
                        onChange={(e) => handleInputMappingChange(paramName, e.target.value)}
                        placeholder={
                          paramDef.default !== undefined
                            ? `Default: ${paramDef.default} (or enter variable name)`
                            : 'Enter variable name or literal value'
                        }
                        className="block w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent-blue font-mono"
                      />
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        Use <code className="bg-card px-1 py-0.5 rounded">$variableName</code> for variables or enter a literal value
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Output Mapping */}
      {hasOutputs && (
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">
            Output Mapping
            <span className="ml-2 text-xs font-normal text-muted-foreground/70">
              Store primitive outputs in scenario variables
            </span>
          </h4>
          <div className="space-y-3">
            {Object.entries(outputProperties).map(([outputName, outputDef]) => (
              <div
                key={outputName}
                className="bg-muted rounded-lg border border-border p-3"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <label
                        htmlFor={`output-${outputName}`}
                        className="text-sm font-medium text-foreground"
                      >
                        {outputName}
                      </label>
                      <span className="text-xs px-1.5 py-0.5 bg-status-success-bg text-status-success rounded font-mono">
                        {outputDef.type}
                      </span>
                    </div>
                    {outputDef.description && (
                      <p className="text-xs text-muted-foreground mb-2">
                        {outputDef.description}
                      </p>
                    )}
                    <input
                      type="text"
                      id={`output-${outputName}`}
                      value={localOutputMapping[outputName] || ''}
                      onChange={(e) => handleOutputMappingChange(outputName, e.target.value)}
                      placeholder="Enter variable name to store result"
                      className="block w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent-blue font-mono"
                    />
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      Variable name to store this output (no $ prefix needed)
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-accent-blue/10 dark:bg-accent-blue/20 border border-accent-blue/30 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-accent-blue mb-2">
          Variable Mapping Tips
        </h5>
        <ul className="text-xs text-accent-blue/90 space-y-1">
          <li>• <strong>Inputs:</strong> Use $variableName to pass a variable value, or enter a literal value directly</li>
          <li>• <strong>Outputs:</strong> Enter a variable name (without $) where the result will be stored</li>
          <li>• Required inputs must be mapped or the primitive will fail validation</li>
          <li>• Unmapped outputs will be discarded</li>
        </ul>
      </div>
    </div>
  );
};

export default PrimitiveMappingEditor;


